import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useField } from 'formik';
import { FC, useCallback } from 'react';
import { useSession } from 'src/contexts/SessionContext';
import {
  PlatformManagedListTagType,
  persistNewPlatformTagListEntry,
} from 'src/models/tags/persistNewPlatformTagListEntry';
import { Tag, createTag, fetchTagsByType } from 'src/models/tags/tags';

import { FormAddableAutocomplete } from '../Fields/FormAutocomplete';

type TagsSelectProps = {
  name: string;
  label?: string;
  placeholder?: string;
  multiple?: boolean;
  tagType?:
    | 'client'
    | 'product'
    | 'package'
    | 'exercise'
    | 'video'
    | 'groupAssessment'
    | 'groupResource'
    | 'location'
    | 'service';
};

export const FormTagsSelect: FC<TagsSelectProps> = ({
  tagType = 'client',
  ...props
}) => {
  const { session } = useSession();
  const queryClient = useQueryClient();
  const [, meta, helpers] = useField(props);
  const { value } = meta;
  const { setValue } = helpers;

  // For location tags, fetch from API; for others, use session data
  const { data: apiTags } = useQuery({
    queryKey: ['tags', tagType],
    queryFn: () => fetchTagsByType(tagType),
    enabled: tagType === 'location',
  });

  const options =
    tagType === 'location'
      ? (apiTags || []).map((tag: Tag) => ({
          id: tag.name,
          title: tag.name,
        }))
      : (session[`${tagType}Tags`] || []).map((tag) => ({
          id: tag,
          title: tag,
        }));

  const handleAddNewTag = useCallback(
    (newTagName: string) => {
      if (tagType === 'location') {
        createTag({
          name: newTagName,
          tagType: 'location',
        }).then(() => {
          queryClient.invalidateQueries({
            queryKey: ['tags', tagType],
          });
        });
      } else {
        void persistNewPlatformTagListEntry(
          session,
          tagType as PlatformManagedListTagType,
          newTagName,
          queryClient
        );
      }
    },
    [tagType, session, queryClient]
  );

  const handleChange = useCallback(
    (value: any) => {
      if (!Array.isArray(value)) {
        setValue([]);
        return;
      }

      // Convert all values to strings and handle new tags
      const processedValues = value
        .map((v) => {
          // New tag being added (has inputValue property)
          if (typeof v === 'object' && v !== null && 'inputValue' in v) {
            const newTagName = v.inputValue as string;
            handleAddNewTag(newTagName);
            return newTagName;
          }
          // Existing tag as string
          if (typeof v === 'string') {
            return v;
          }
          // Existing tag as object with title
          if (typeof v === 'object' && v !== null && 'title' in v) {
            return v.title as string;
          }
          return '';
        })
        .filter(Boolean);

      setValue(processedValues);
    },
    [setValue, handleAddNewTag]
  );

  return (
    <FormAddableAutocomplete
      {...props}
      options={options}
      value={value}
      onChange={handleChange as any}
      keepUnmatched
    />
  );
};
